<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Skeleton } from '$lib/components/ui/skeleton';
  import AdaptForecastDialog from '../MyFitness/Calendar/AdaptForecastDialog.svelte';
  import ReportHero from './ReportHero.svelte';
  import ShareButtons from './ShareButtons.svelte';
  import ReportOverviewTab from './ReportOverviewTab.svelte';
  import ReportProgressTab from './ReportProgressTab.svelte';
  import ReportExecutionTab from './ReportExecutionTab.svelte';
  import ReportJourneyTab from './ReportJourneyTab.svelte';
  import ReportVersionsTab from './ReportVersionsTab.svelte';
  import { getPlanReport } from '@/Util/api';
  import { getRequest } from '@/Util/utils';
  import { isStatusReport } from './report';

  // A stored plan's id for its completion report, or 'current' for the status of the plan in progress.
  export let planId = 'current';
  export let forUser: string | null = null;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  let report: any = null;
  let loading = true;
  let error: string | null = null;
  let notAvailable = false;
  // Counts up when the report was read again after a recalculation of the athlete's history (the tabs clear what they said about it).
  let refreshed = 0;
  $: isStatus = isStatusReport(report);

  let tab = 'overview';
  // ?print=1 is the page as the PDF is made from it: every tab laid out, and a signal when it is drawn.
  const printMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('print') === '1';
  let printing = printMode;   // every tab is laid out when the page is printed
  function announceReady() {
    // @ts-ignore read by tools/report-pdf.js
    if (printMode) setTimeout(() => (window.__reportReady = true), 3000);   // the charts of every tab need a moment to draw
  }

  async function load(fresh = false) {
    try {
      const result = await getPlanReport(planId, forUser, fresh);
      if (result === null || result.available === false) notAvailable = true;
      else {
        report = result.report;
        if (fresh && !recalculating) refreshed += 1;
      }
    } catch (e) {
      if (!fresh) error = e instanceof Error ? e.message : $_('report.page.load_failed');
    } finally {
      loading = false;
      announceReady();
    }
  }

  // A change to the athlete's history (flagging an activity, applying a breakthrough) is recalculated in the background.
  // The server announces when it is done; the report then reads again, without the copy it keeps for ten minutes. If no
  // announcement arrives (no live connection), it reads again after a minute and a half.
  let recalculating = false;
  let fallback: ReturnType<typeof setTimeout> | null = null;
  const recalculationDone = () => {
    if (!recalculating) return;
    recalculating = false;
    if (fallback) clearTimeout(fallback);
    readAgain();
  };
  // A stored completion report keeps what it was built from: after the history was recalculated it is rebuilt (as the Rebuild
  // button does) before it is read. A status report is built each time it is read.
  async function readAgain() {
    if (!isStatus) {
      try {
        await window.fetch(`/plan-report/${encodeURIComponent(planId)}/rebuild${forUser ? `?forUser=${encodeURIComponent(forUser)}` : ''}`, { method: 'POST', ...getRequest(), body: JSON.stringify({}) });
      } catch (e) {
        // the stored report is read as it is
      }
    }
    await load(true);
  }
  function awaitRecalculation() {
    recalculating = true;
    if (fallback) clearTimeout(fallback);
    fallback = setTimeout(recalculationDone, 90000);
  }

  let adaptDialog: any = null;
  // Adapt Forecast is offered when it is lit on the Today page: availability changed since the plan was saved, or a day conflicts.
  let adaptLit = false;

  onMount(() => {
    load();
    //@ts-ignore
    const channel = window.Xert?._userChannel;
    channel?.bind('trickle_complete', recalculationDone);
    return () => {
      channel?.unbind('trickle_complete', recalculationDone);
      if (fallback) clearTimeout(fallback);
    };
  });

  const tabs = [
    { key: 'overview', label: 'report.tabs.overview' },
    { key: 'progress', label: 'report.tabs.progress' },
    { key: 'execution', label: 'report.tabs.execution' },
    { key: 'journey', label: 'report.tabs.journey' },
    { key: 'adaptations', label: 'report.tabs.versions' },
  ];
  const show = (key: string, isPrinting: boolean, current: string) => isPrinting || current === key;
  // Arrow keys move between the tabs, as a tab list does.
  function onTabKey(e: KeyboardEvent) {
    const i = tabs.findIndex((t) => t.key === tab);
    const next = e.key === 'ArrowRight' ? (i + 1) % tabs.length : e.key === 'ArrowLeft' ? (i + tabs.length - 1) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    tab = tabs[next].key;
    tick().then(() => document.getElementById('tab-' + tab)?.focus());
  }
</script>

<svelte:window on:beforeprint={() => (printing = true)} on:afterprint={() => (printing = false)} />

<div class="mx-auto max-w-6xl px-4 py-6 text-zinc-800">
  {#if loading}
    <Skeleton class="h-10 w-2/3 mb-4" />
    <Skeleton class="h-48 w-full mb-4" />
    <Skeleton class="h-72 w-full" />
  {:else if error}
    <div class="rounded border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
  {:else if notAvailable}
    <div class="rounded border bg-white p-6">
      <h1 class="text-xl font-semibold mb-1">{planId === 'current' ? $_('report.page.no_plan') : /^\d{4}-\d{2}-\d{2}$/.test(planId) ? $_('report.page.no_report_date') : $_('report.page.no_report')}</h1>
      <p class="text-zinc-500">{planId === 'current' ? $_('report.page.create_plan') : /^\d{4}-\d{2}-\d{2}$/.test(planId) ? $_('report.page.none_on_date') : $_('report.page.no_report_for_plan')}</p>
    </div>
  {:else if report}
    <div class="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between print:hidden">
      <a class="text-sm text-blue-600" href="/my-fitness#program">{$_('report.page.back')}</a>
      <ShareButtons {planId} {forUser} on:rebuilt={() => load(true)} />
    </div>
    <ReportHero {report} />

    <div class="relative mt-4 mb-6 print:hidden">
    <nav class="grid grid-cols-3 gap-x-1 md:flex md:gap-1 md:overflow-x-auto md:overflow-y-hidden border-b border-zinc-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label={$_('report.page.sections')} on:keydown={onTabKey}>
      {#each tabs as t}
        <button role="tab" id="tab-{t.key}" aria-controls="panel-{t.key}" tabindex={tab === t.key ? 0 : -1} aria-selected={tab === t.key} class="px-2 md:px-4 py-2.5 text-sm text-center md:whitespace-nowrap bg-transparent border-0 border-b-2 border-solid -mb-px {tab === t.key ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-zinc-600 hover:text-zinc-900'}" on:click={() => (tab = t.key)}>{$_(t.label)}</button>
      {/each}
    </nav>
    </div>

    <div id="panel-{tab}" role="tabpanel" aria-labelledby="tab-{tab}">
      {#if show('overview', printing, tab)}
        <ReportOverviewTab {report} {forUser} onAdapt={!forUser && adaptLit ? () => adaptDialog.openModal() : null} onApplied={awaitRecalculation} onDismissed={() => load(true)} />
      {/if}
      {#if show('progress', printing, tab)}
        <ReportProgressTab {report} {forUser} onFlagged={awaitRecalculation} {refreshed} />
      {/if}
      {#if show('execution', printing, tab) && report.execution}
        <ReportExecutionTab {report} />
      {/if}
      {#if show('journey', printing, tab)}
        <ReportJourneyTab {report} />
      {/if}
      {#if show('adaptations', printing, tab)}
        <ReportVersionsTab {report} />
      {/if}
    </div>
  {/if}
  <!-- Only a status report offers Adapt Forecast, and the PDF is made from the page without it. -->
  {#if !forUser && isStatus && !printMode}
    <AdaptForecastDialog bind:this={adaptDialog} watch on:lit={(e) => (adaptLit = e.detail)} on:saved={() => load(true)} on:error={(e) => (error = e.detail)} />
  {/if}
</div>
